import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function ProspectQuotePrint({
  prospect,
  currentQuote,
  quoteItems,
  totals,
  onClose
}) {

  const [companySettings, setCompanySettings] = useState({
    company_name: 'DoorBase',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    phone: '',
    email: '',
    website: 'doorbase.co.uk',
    vat_number: ''
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const res = await apiFetch('/api/company-settings');
      const data = await res.json();
      if (data) {
        setCompanySettings(data);
      }
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-auto">
      {/* Print Buttons - Hidden when printing */}
      <div className="no-print bg-gray-100 border-b border-gray-300 p-4 flex justify-between items-center sticky top-0 z-10">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          ← Back to Editor
        </button>
        <button
          onClick={handlePrint}
          className="px-6 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 font-bold"
        >
          🖨️ Print / Save as PDF
        </button>
      </div>

      {/* Printable Quote Document */}
      <div className="max-w-[210mm] mx-auto bg-white p-8">
        
        {/* Company Header */}
        <div className="border-b-4 border-teal-600 pb-6 mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{companySettings.company_name}</h1>
              <p className="text-sm text-gray-600 mt-1">Commercial Door & Automation Solutions</p>
              {companySettings.address_line1 && (
                <div className="text-sm text-gray-600 mt-2">
                  <div>{companySettings.address_line1}</div>
                  {companySettings.address_line2 && <div>{companySettings.address_line2}</div>}
                  <div>{companySettings.city} {companySettings.postcode}</div>
                </div>
              )}
              {companySettings.phone && (
                <p className="text-sm text-gray-600">Tel: {companySettings.phone}</p>
              )}
              {companySettings.email && (
                <p className="text-sm text-gray-600">Email: {companySettings.email}</p>
              )}
              {companySettings.vat_number && (
                <p className="text-sm text-gray-600">VAT No: {companySettings.vat_number}</p>
              )}
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-teal-600">QUOTATION</div>
              <div className="text-sm text-gray-600 mt-2">
                <div>Date: {formatDate(currentQuote.quote_date)}</div>
                <div>Valid Until: {formatDate(currentQuote.valid_until)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Client & Site Details */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">Quote For:</h3>
            <div className="text-gray-900">
              <div className="font-semibold text-lg">{prospect.prospect_name}</div>
              {prospect.contact_name && (
                <div className="text-sm mt-1">Attn: {prospect.contact_name}</div>
              )}
              {prospect.email && (
                <div className="text-sm">{prospect.email}</div>
              )}
              {prospect.phone && (
                <div className="text-sm">{prospect.phone}</div>
              )}
            </div>
          </div>

          {currentQuote.work_location && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">Work Location:</h3>
              <div className="text-gray-900 text-sm">
                {currentQuote.work_location}
              </div>
            </div>
          )}
        </div>

        {/* Description of Works */}
        {currentQuote.description && (
          <div className="mb-8 p-4 bg-gray-50 rounded border border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">Description of Works:</h3>
            <div className="text-gray-900 text-sm whitespace-pre-wrap leading-relaxed">
              {currentQuote.description}
            </div>
          </div>
        )}

        {/* Line Items Table */}
        {quoteItems.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-3">Items:</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="text-left px-3 py-2 text-sm font-semibold border border-teal-700">Description</th>
                  <th className="text-center px-3 py-2 text-sm font-semibold border border-teal-700 w-16">Qty</th>
                  <th className="text-center px-3 py-2 text-sm font-semibold border border-teal-700 w-20">Unit</th>
                  <th className="text-right px-3 py-2 text-sm font-semibold border border-teal-700 w-24">Price</th>
                  <th className="text-right px-3 py-2 text-sm font-semibold border border-teal-700 w-28">Total</th>
                </tr>
              </thead>
              <tbody>
                {quoteItems.map((item, index) => {
                  const qty = parseFloat(item.quantity) || 0;
                  const price = parseFloat(item.sell_price || item.unit_price) || 0;
                  const lineTotal = qty * price;
                  
                  return (
                    <tr key={index} className="border-b border-gray-300">
                      <td className="px-3 py-2 text-sm border-l border-r border-gray-300">
                        {item.description}
                        {item.category && item.category !== 'parts' && (
                          <span className="text-xs text-gray-500 ml-2">({item.category})</span>
                        )}
                      </td>
                      <td className="text-center px-3 py-2 text-sm border-r border-gray-300">
                        {qty}
                      </td>
                      <td className="text-center px-3 py-2 text-sm border-r border-gray-300">
                        {item.unit}
                      </td>
                      <td className="text-right px-3 py-2 text-sm border-r border-gray-300">
                        £{price.toFixed(2)}
                      </td>
                      <td className="text-right px-3 py-2 text-sm font-medium border-r border-gray-300">
                        £{lineTotal.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-80">
            <div className="flex justify-between py-2 border-b border-gray-300">
              <span className="font-medium">Subtotal:</span>
              <span>£{totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-300">
              <span className="font-medium">VAT (20%):</span>
              <span>£{totals.vatAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3 bg-teal-600 text-white px-3 font-bold text-lg mt-1">
              <span>TOTAL:</span>
              <span>£{totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Terms & Conditions */}
        {currentQuote.terms && (
          <div className="mb-8 p-4 bg-gray-50 rounded border border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">Terms & Conditions:</h3>
            <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
              {currentQuote.terms}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t-2 border-gray-300 pt-6 mt-8 text-center text-xs text-gray-600">
          <p className="mb-2">
            For any queries regarding this quotation, please contact us:
          </p>
          <p className="font-medium">
            {companySettings.email && `Email: ${companySettings.email}`}
            {companySettings.email && companySettings.phone && ' | '}
            {companySettings.phone && `Phone: ${companySettings.phone}`}
          </p>
          {companySettings.website && (
            <p className="mt-1">{companySettings.website}</p>
          )}
          <p className="mt-4 text-gray-500">
            This quotation is valid until {formatDate(currentQuote.valid_until)}
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            size: A4;
            margin: 15mm;
          }
        }
      `}</style>
    </div>
  );
}

export default ProspectQuotePrint;
